/**
 * Replay a CTA click captured before the app shell booted.
 *
 * What: Stops recording when the interactive tree commits, then consumes and
 * replays the pending intent on the next frame against the replacement CTA.
 * Why: On lean marketing pages the shell only boots on first interaction, so
 * the click that triggered the boot happened on the static snapshot — without
 * replay it would be silently lost (which is also today's pre-mount behavior).
 * When: Update alongside the capture contract in
 * `src/utils/leanMarketingBoot.ts`.
 */
import { useLayoutEffect } from 'react';

import {
  consumeLeanBootCtaIntent,
  LEAN_BOOT_CTA_ATTR,
  stopLeanBootCtaRecording,
} from '@/utils/leanMarketingBoot';

/**
 * Mounted from shared marketing chrome (LandingTopBar) so every current and
 * future lean-boot route replays pre-boot clicks without extra wiring. No-op
 * when nothing is pending — i.e. on every normal boot.
 */
export function useLeanBootCtaReplay(): void {
  useLayoutEffect(() => {
    // React now owns the controls. A real click between commit and replay
    // must not be captured again and subsequently fired twice.
    stopLeanBootCtaRecording();
    if (!window.__DT_LEAN_CTA__) {
      return undefined;
    }

    // Consume only when replay executes: StrictMode's effect cleanup can
    // cancel this frame, but the next setup must still find the same intent.
    const frame = requestAnimationFrame(() => {
      const intent = consumeLeanBootCtaIntent();
      if (!intent) return;

      const matches = document.querySelectorAll<HTMLElement>(
        `[${LEAN_BOOT_CTA_ATTR}="${intent.value}"]`
      );
      const target = matches[intent.index] ?? matches[0];
      target?.focus({ preventScroll: true });
      target?.click();
    });

    return () => cancelAnimationFrame(frame);
  }, []);
}
